import { PortalDsBadge } from 'src/components/portal-design-system';
import type { PortalDsBadgeVariant } from 'src/components/portal-design-system';
import { formatDisplayDate } from 'src/helpers/date.helpers';
import type { PortalUserDetail } from '../types/users.types';
import {
  formatStatusLabel,
  formatSubscriptionLabel,
  getSubscriptionBadgeVariant,
  resolvePortalUserStatus,
} from '../utils/portal-users.mappers';

const STATUS_VARIANTS: Record<PortalUserDetail['status'], PortalDsBadgeVariant> = {
  active: 'green',
  blocked: 'red',
};

interface UserDetailOverviewTabProps {
  user: PortalUserDetail;
  onOpenTeam?: (workspaceId: string) => void;
}

function DetailField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-[12px] font-medium uppercase tracking-[0.04em] text-[#9CA3AF]">
        {label}
      </dt>
      <dd className="text-[14px] text-[#0D0F1A]">
        {children}
      </dd>
    </div>
  );
}

export function UserDetailOverviewTab({ user, onOpenTeam }: UserDetailOverviewTabProps) {
  const workspaceName = user.workspace_name ?? null;
  const status = resolvePortalUserStatus(user);

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[16px] font-semibold text-[#0D0F1A]">
          {user.name}
        </span>
        {user.is_guest ? (
          <PortalDsBadge variant="amber" pill>
            Guest
          </PortalDsBadge>
        ) : null}
        <PortalDsBadge
          variant={STATUS_VARIANTS[status]}
          pill
        >
          {formatStatusLabel(status)}
        </PortalDsBadge>
        <PortalDsBadge
          variant={getSubscriptionBadgeVariant(user.subscription)}
          pill
        >
          {formatSubscriptionLabel(user.subscription)}
        </PortalDsBadge>
      </div>

      <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <DetailField label="Email">
          <a
            href={`mailto:${user.email}`}
            className="text-[#2563EB] hover:underline"
          >
            {user.email}
          </a>
        </DetailField>

        <DetailField label="Workspace / team">
          {user.workspace_id ? (
            onOpenTeam ? (
              <button
                type="button"
                className="text-left text-[#2563EB] hover:underline"
                onClick={() => onOpenTeam(user.workspace_id!)}
              >
                {workspaceName ?? user.workspace_id}
              </button>
            ) : (
              <span>
                {workspaceName ?? user.workspace_id}
              </span>
            )
          ) : (
            <span className="text-[#9CA3AF]">
              —
            </span>
          )}
        </DetailField>

        <DetailField label="Created">
          {user.created_at ? formatDisplayDate(user.created_at) : '—'}
        </DetailField>

        <DetailField label="Pending campaigns">
          {user.pending_campaigns === 0 ? (
            <span className="text-[#9CA3AF]">
              —
            </span>
          ) : (
            <span className="font-mono text-[13px]">
              {user.pending_campaigns}
            </span>
          )}
        </DetailField>
      </dl>
    </section>
  );
}
